import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ProductV2, ProductFormData } from "../types";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import Text from "@/components/ui/text";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import DeleteProductButton from "@/components/ui/delete-product-button";
import { useTranslation } from "react-i18next";

const TRANSLATION_KEY = "settings.product";

interface ProductDrawerProps {
  isOpen: boolean;
  product?: ProductV2;
  /** Product's position index — drives the icon color to match its card. */
  index?: number;
  onClose: () => void;
  onSave: (data: ProductFormData) => void;
  onDelete?: (productId: string) => void;
  accountName?: string;
  totalProductCount?: number;
}

const ProductDrawer: React.FC<ProductDrawerProps> = ({
  isOpen,
  product,
  index = 0,
  onClose,
  onSave,
  onDelete,
  accountName = "Organization",
  totalProductCount = 1
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [productName, setProductName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [_isFormValid, setIsFormValid] = useState<boolean>();
  const [formSetValue, setFormSetValue] = useState<any>(null);

  useEffect(() => {
    if (isOpen && product) {
      setProductName(product.name);
      setTimeout(() => {
        if (formSetValue) {
          formSetValue("productName", product.name);
          formSetValue("productDescription", product.description || "");
        }
      }, 100);
    }
  }, [isOpen, product, formSetValue]);

  useEffect(() => {
    if (!isOpen) {
      setProductName("");
    }
  }, [isOpen]);

  const sections: FormSectionProps[] = [
    {
      id: "productInfo",
      divider: false,
      fields: [
        {
          name: "productName",
          label: t(`${TRANSLATION_KEY}.productDrawer.productName`),
          type: "text",
          required: true,
          fieldActionProps: {
            placeholder: t(
              `${TRANSLATION_KEY}.productDrawer.productNamePlaceholder`
            ),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              setProductName(e.target.value)
          },
          defaultValue: product ? product.name : undefined
        },
        {
          name: "productDescription",
          label: t(`${TRANSLATION_KEY}.productDrawer.productDescription`),
          type: "textarea",
          required: false,
          fieldActionProps: {
            placeholder: t(
              `${TRANSLATION_KEY}.productDrawer.productDescriptionPlaceholder`
            )
          },
          defaultValue: product ? product.description : undefined
        }
      ]
    }
  ];

  const handleSubmitButton = () => {
    formRef.current?.requestSubmit();
  };

  const handleSubmit = async (data: any) => {
    const formData: ProductFormData = {
      name: data.productName,
      description: data.productDescription || ""
    };
    onSave(formData);
  };

  const handleDeleteProduct = () => {
    if (product && onDelete) {
      onDelete(product.id);
      setShowDeleteModal(false);
      onClose();
    }
  };

  return (
    <Drawer
      open={isOpen}
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={false}
      noBodyStyles={true}
      dismissible={false}
    >
      <AppDrawerContent
        className="p-0 overflow-x-hidden w-full"
        data-test="products-drawer"
        isSettingsDrawer
      >
        <DrawerHeader>
          <DrawerTitle>
            <LogoIconContainer
              index={index}
              className="size-8 rounded-[8px]"
              innerClassName="rounded-[8px]"
              iconSize="5"
            />
            <Text
              variant={productName ? "lgSubtitle" : "lgSubtitleTertiary"}
              data-test="products-drawer-title"
            >
              {productName ||
                `${accountName} ${t(`${TRANSLATION_KEY}.drawer.defaultProductName`)}`}
            </Text>
          </DrawerTitle>
          <IconButton
            onClick={onClose}
            variant="iconNoBg"
            size="md"
            data-test="products-drawer-close-button"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="p-5 w-full overflow-y-scroll">
          <FormNew
            sections={sections}
            isFormValid={(isValid) => {
              setIsFormValid(isValid);
            }}
            onSubmit={handleSubmit}
            formRef={formRef}
            onSetValue={({ setValue }) => setFormSetValue(() => setValue)}
          />
        </div>

        <DrawerFooter>
          <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
            {product && onDelete && (
              <DeleteProductButton
                label={t("products.deleteProduct")}
                disabled={totalProductCount === 1}
                onClick={() => setShowDeleteModal(true)}
                tooltipText={
                  totalProductCount === 1
                    ? t(`${TRANSLATION_KEY}.productDrawer.deleteTooltip`)
                    : undefined
                }
                data-test="products-drawer-delete-button"
              />
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                variant="secondaryNoBg"
                onClick={onClose}
                data-test="products-drawer-cancel-button"
              >
                {t("cancel")}
              </Button>
              <Button
                disabled={!productName}
                onClick={handleSubmitButton}
                data-test="products-drawer-submit-button"
              >
                {product ? t("save") : t(`${TRANSLATION_KEY}.addProduct`)}
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </AppDrawerContent>

      <ConfirmationModal
        isOpen={showDeleteModal}
        setIsOpen={setShowDeleteModal}
        title={t(`${TRANSLATION_KEY}.productDrawer.deleteModal.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY}.productDrawer.deleteModal.confirmationText`
        )}
        handleSubmit={handleDeleteProduct}
        submitButtonText={t(
          `${TRANSLATION_KEY}.productDrawer.deleteModal.submitButtonText`
        )}
        hideDivider
        data-test="products-drawer-delete-modal"
      />
    </Drawer>
  );
};

export default ProductDrawer;
